/**
 * React-Query Hooks für die 8 Referenz-Tabellen.
 *
 * Eine generische ``useRefList`` / ``useRefMutations`` Familie pro Tabelle.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/auth/apiClient';
import type {
  BetreuungsmodellForm,
  BetreuungsmodellRow,
  FoerderprogrammForm,
  FoerderprogrammRow,
  ISODate,
  KonstanteForm,
  KonstanteRow,
  PauschaleLandForm,
  PauschaleLandRow,
  PauschaleTraegerForm,
  PauschaleTraegerRow,
  SachkontoForm,
  SachkontoGruppeForm,
  SachkontoGruppeRow,
  SachkontoRow,
  SueZulageForm,
  SueZulageRow,
  SvSatzForm,
  SvSatzRow,
  TvoedForm,
  TvoedRow,
  TvoedVkaForm,
  TvoedVkaRow,
  UUID,
} from './refTypes';

const REF_ENDPOINTS = {
  tvoed: '/admin/ref/tvoed',
  tvoed_vka: '/admin/ref/tvoed-vka',
  sue_zulage: '/admin/ref/sue-zulage',
  sv_satz: '/admin/ref/sv-satz',
  betreuungsmodell: '/admin/ref/betreuungsmodell',
  pauschale_land: '/admin/ref/pauschale-land',
  pauschale_traeger: '/admin/ref/pauschale-traeger',
  foerderprogramm: '/admin/ref/foerderprogramm',
  konstante: '/admin/ref/konstante',
  sachkonto_gruppe: '/admin/ref/sachkonto-gruppe',
  sachkonto: '/admin/ref/sachkonto',
} as const;

export type RefTable = keyof typeof REF_ENDPOINTS;

export const refQueryKey = (table: RefTable, stichtag: ISODate | null) =>
  ['admin', 'ref', table, stichtag] as const;

function buildParams(stichtag: ISODate | null): Record<string, string> {
  return stichtag ? { stichtag } : {};
}

function useRefList<Row>(table: RefTable, stichtag: ISODate | null) {
  return useQuery({
    queryKey: refQueryKey(table, stichtag),
    queryFn: async (): Promise<Row[]> => {
      const res = await apiClient.get<Row[]>(REF_ENDPOINTS[table], {
        params: buildParams(stichtag),
      });
      return res.data;
    },
  });
}

function useRefCreate<Form, Row>(table: RefTable) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Form): Promise<Row> => {
      const res = await apiClient.post<Row>(REF_ENDPOINTS[table], body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'ref', table] });
    },
  });
}

function useRefUpdate<Form, Row>(table: RefTable) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: UUID; body: Form }): Promise<Row> => {
      const res = await apiClient.put<Row>(`${REF_ENDPOINTS[table]}/${id}`, body);
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'ref', table] });
    },
  });
}

function useRefDelete(table: RefTable) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID): Promise<void> => {
      await apiClient.delete(`${REF_ENDPOINTS[table]}/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'ref', table] });
    },
  });
}

// Typed exports per table
export const useTvoedList = (stichtag: ISODate | null) =>
  useRefList<TvoedRow>('tvoed', stichtag);
export const useTvoedCreate = () => useRefCreate<TvoedForm, TvoedRow>('tvoed');
export const useTvoedUpdate = () => useRefUpdate<TvoedForm, TvoedRow>('tvoed');
export const useTvoedDelete = () => useRefDelete('tvoed');

export const useTvoedVkaList = (stichtag: ISODate | null) =>
  useRefList<TvoedVkaRow>('tvoed_vka', stichtag);
export const useTvoedVkaCreate = () =>
  useRefCreate<TvoedVkaForm, TvoedVkaRow>('tvoed_vka');
export const useTvoedVkaUpdate = () =>
  useRefUpdate<TvoedVkaForm, TvoedVkaRow>('tvoed_vka');
export const useTvoedVkaDelete = () => useRefDelete('tvoed_vka');

export const useSueZulageList = (stichtag: ISODate | null) =>
  useRefList<SueZulageRow>('sue_zulage', stichtag);
export const useSueZulageCreate = () =>
  useRefCreate<SueZulageForm, SueZulageRow>('sue_zulage');
export const useSueZulageUpdate = () =>
  useRefUpdate<SueZulageForm, SueZulageRow>('sue_zulage');
export const useSueZulageDelete = () => useRefDelete('sue_zulage');

export const useSvSatzList = (stichtag: ISODate | null) =>
  useRefList<SvSatzRow>('sv_satz', stichtag);
export const useSvSatzCreate = () => useRefCreate<SvSatzForm, SvSatzRow>('sv_satz');
export const useSvSatzUpdate = () => useRefUpdate<SvSatzForm, SvSatzRow>('sv_satz');
export const useSvSatzDelete = () => useRefDelete('sv_satz');

export const useBetreuungsmodellList = () =>
  useRefList<BetreuungsmodellRow>('betreuungsmodell', null);
export const useBetreuungsmodellCreate = () =>
  useRefCreate<BetreuungsmodellForm, BetreuungsmodellRow>('betreuungsmodell');
export const useBetreuungsmodellUpdate = () =>
  useRefUpdate<BetreuungsmodellForm, BetreuungsmodellRow>('betreuungsmodell');
export const useBetreuungsmodellDelete = () => useRefDelete('betreuungsmodell');

export const usePauschaleLandList = (stichtag: ISODate | null) =>
  useRefList<PauschaleLandRow>('pauschale_land', stichtag);
export const usePauschaleLandCreate = () =>
  useRefCreate<PauschaleLandForm, PauschaleLandRow>('pauschale_land');
export const usePauschaleLandUpdate = () =>
  useRefUpdate<PauschaleLandForm, PauschaleLandRow>('pauschale_land');
export const usePauschaleLandDelete = () => useRefDelete('pauschale_land');

export const usePauschaleTraegerList = (stichtag: ISODate | null) =>
  useRefList<PauschaleTraegerRow>('pauschale_traeger', stichtag);
export const usePauschaleTraegerCreate = () =>
  useRefCreate<PauschaleTraegerForm, PauschaleTraegerRow>('pauschale_traeger');
export const usePauschaleTraegerUpdate = () =>
  useRefUpdate<PauschaleTraegerForm, PauschaleTraegerRow>('pauschale_traeger');
export const usePauschaleTraegerDelete = () => useRefDelete('pauschale_traeger');

export const useFoerderprogrammList = (stichtag: ISODate | null) =>
  useRefList<FoerderprogrammRow>('foerderprogramm', stichtag);
export const useFoerderprogrammCreate = () =>
  useRefCreate<FoerderprogrammForm, FoerderprogrammRow>('foerderprogramm');
export const useFoerderprogrammUpdate = () =>
  useRefUpdate<FoerderprogrammForm, FoerderprogrammRow>('foerderprogramm');
export const useFoerderprogrammDelete = () => useRefDelete('foerderprogramm');

export const useKonstanteList = (stichtag: ISODate | null) =>
  useRefList<KonstanteRow>('konstante', stichtag);
export const useKonstanteCreate = () =>
  useRefCreate<KonstanteForm, KonstanteRow>('konstante');
export const useKonstanteUpdate = () =>
  useRefUpdate<KonstanteForm, KonstanteRow>('konstante');
export const useKonstanteDelete = () => useRefDelete('konstante');

// Sachkonto-Katalog — Gruppen (Ebene 1a) und Konten (Ebene 1b). Ueber die
// Referenzdaten-Tabs erreichbar, damit sich der Kontenrahmen erweitern
// laesst, ohne eine Migration zu schreiben (W2 der Fixwelle vom
// 2026-08-06).
export const useSachkontoGruppeList = (stichtag: ISODate | null) =>
  useRefList<SachkontoGruppeRow>('sachkonto_gruppe', stichtag);
export const useSachkontoGruppeCreate = () =>
  useRefCreate<SachkontoGruppeForm, SachkontoGruppeRow>('sachkonto_gruppe');
export const useSachkontoGruppeUpdate = () =>
  useRefUpdate<SachkontoGruppeForm, SachkontoGruppeRow>('sachkonto_gruppe');
export const useSachkontoGruppeDelete = () => useRefDelete('sachkonto_gruppe');

export const useSachkontoList = (stichtag: ISODate | null) =>
  useRefList<SachkontoRow>('sachkonto', stichtag);
export const useSachkontoCreate = () =>
  useRefCreate<SachkontoForm, SachkontoRow>('sachkonto');
export const useSachkontoUpdate = () =>
  useRefUpdate<SachkontoForm, SachkontoRow>('sachkonto');
export const useSachkontoDelete = () => useRefDelete('sachkonto');
